import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AdminShell } from "@/components/admin/admin-shell";
import { ShipmentStatusPill } from "@/components/admin/status-badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useShipments } from "@/lib/queries";
import { formatDateTime, formatUGX, locationLabel } from "@/lib/format";
import { ACTIVE_STATUSES } from "@/lib/types";
import { useUIStore } from "@/store/ui";

export const Route = createFileRoute("/shipments")({
  component: ShipmentsPage,
});

type Filter = "all" | "active" | "completed" | "cancelled";

function ShipmentsPage() {
  const { data: shipments, isLoading } = useShipments();
  const [filter, setFilter] = useState<Filter>("all");
  const selectedId = useUIStore((s) => s.selectedShipmentId);
  const setSelectedId = useUIStore((s) => s.setSelectedShipmentId);

  // Set page title on mount
  useEffect(() => {
    document.title = "Shipments — 24Boda Admin";
  }, []);

  const rows = (shipments ?? []).filter((s) => {
    if (filter === "active") return ACTIVE_STATUSES.includes(s.status);
    if (filter === "completed") return s.status === "delivered";
    if (filter === "cancelled") return s.status === "cancelled" || s.status === "failed";
    return true;
  });

  const selected = (shipments ?? []).find((s) => s.id === selectedId) ?? null;

  return (
    <AdminShell title="Shipments" description="All deliveries across the platform">
      <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="overflow-x-auto rounded-lg p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Pickup</TableHead>
              <TableHead>Dropoff</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Distance</TableHead>
              <TableHead className="text-right">Price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  Loading shipments…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No shipments in this view.
                </TableCell>
              </TableRow>
            )}
            {rows.map((s) => (
              <TableRow key={s.id} onClick={() => setSelectedId(s.id)} className="cursor-pointer">
                <TableCell className="whitespace-nowrap">{formatDateTime(s.createdAt)}</TableCell>
                <TableCell className="max-w-[200px] truncate">{locationLabel(s.pickup)}</TableCell>
                <TableCell className="max-w-[200px] truncate">{locationLabel(s.dropoff)}</TableCell>
                <TableCell>
                  <ShipmentStatusPill status={s.status} />
                </TableCell>
                <TableCell className="text-right">
                  {(s.routeDistanceMeters / 1000).toFixed(1)} km
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatUGX(s.price.customerTotalUgx)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelectedId(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle>Shipment details</DialogTitle>
            <DialogDescription>{selected?.id}</DialogDescription>
          </DialogHeader>
          {selected && (
            <dl className="space-y-3 text-sm">
              <Row label="Status" value={<ShipmentStatusPill status={selected.status} />} />
              <Row label="Pickup" value={locationLabel(selected.pickup)} />
              <Row label="Dropoff" value={locationLabel(selected.dropoff)} />
              <Row label="Package size" value={selected.package.size} />
              <Row
                label="Handling"
                value={selected.package.handlingRequirements.join(", ") || "Standard"}
              />
              <Row
                label="Distance"
                value={`${(selected.routeDistanceMeters / 1000).toFixed(1)} km`}
              />
              <Row
                label="Est. duration"
                value={`${Math.ceil(selected.routeDurationSeconds / 60)} min`}
              />
              <Row label="Customer" value={selected.customerId || "—"} />
              <Row label="Rider" value={selected.assignedRiderId || "Unassigned"} />
              <Row label="Fare" value={formatUGX(selected.price.customerTotalUgx)} />
              <Row label="Rider earnings" value={formatUGX(selected.price.riderEarningUgx)} />
              <Row
                label="Platform commission"
                value={formatUGX(selected.price.platformCommissionUgx)}
              />
              <Row label="Created" value={formatDateTime(selected.createdAt)} />
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border pb-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}
